import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { formatPrice, formatHour } from "@/lib/utils";
import { toast } from "sonner";
import { Search, ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";

export default function AdminBookings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const userIdFilter = searchParams.get("user_id") || "";
  const LIMIT = 20;

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT, sort: "created_at", order: "desc" };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (userIdFilter) params.user_id = userIdFilter;
      const r = await api.get("/admin/bookings", { params });
      setBookings(r.data.data);
      setTotal(r.data.total);
      setPages(r.data.pages);
    } catch {
      toast.error(t("common.registration_failed"));
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, userIdFilter, t]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const [searchInput, setSearchInput] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const headers = [t("admin.th.player"), t("admin.th.court"), t("admin.th.date"), t("admin.th.time"), t("admin.th.total"), t("admin.th.status_col"), t("admin.created")];

  return (
    <div className="min-h-[calc(100vh-64px)] bg-background">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-muted-foreground hover:text-volt transition-colors mb-4">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>

        <div className="label-eyebrow text-volt mb-2">{t("admin.eyebrow")}</div>
        <h1 className="font-heading text-5xl md:text-6xl tracking-tight mb-10">{t("admin.tabs.bookings")}</h1>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t("admin.search_placeholder")}
              className="w-full bg-card border border-border ps-10 pe-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/40 focus:border-volt outline-none transition-colors"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="bg-card border border-border rounded-sm px-3 py-2.5 text-sm text-foreground/80 focus:border-volt outline-none uppercase tracking-wider appearance-none cursor-pointer"
          >
            <option value="">{t("admin.filter_all_status")}</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <span className="text-xs text-muted-foreground/60">{t("admin.total_items", { total })}</span>
        </div>

        <div className="border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-background">
              <tr>
                {headers.map((h) => (
                  <th key={h} className="text-start p-3 text-[10px] tracking-widest uppercase text-muted-foreground/80 font-bold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={headers.length} className="p-6 text-center text-muted-foreground/60 text-xs">{t("common.loading")}</td></tr>
              ) : bookings.length === 0 ? (
                <tr><td colSpan={headers.length} className="p-6 text-center text-muted-foreground/60 text-xs">{t("admin.no_results")}</td></tr>
              ) : bookings.map((b) => (
                <tr
                  key={b.id}
                  className="border-t border-border/30 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => navigate(`/bookings/${b.id}`)}
                >
                  <td className="p-3">{b.user_name} {b.is_manual && <span className="inline-block text-[9px] tracking-wider uppercase font-bold px-1.5 py-0.5 bg-volt/15 text-volt rounded-sm ml-1">Manual</span>}</td>
                  <td className="p-3">{b.court_name}</td>
                  <td className="p-3">{b.date}</td>
                  <td className="p-3 font-mono text-xs">{formatHour(b.start_hour)}-{formatHour(b.end_hour)}</td>
                  <td className="p-3 text-volt font-bold">{formatPrice(b.total_price, b.currency)}</td>
                  <td className="p-3">
                    <span className={`text-[10px] tracking-widest uppercase font-bold ${
                      b.status === "confirmed" ? "text-emerald-400" :
                      b.status === "cancelled" ? "text-red-400" : "text-yellow-400"
                    }`}>
                      {b.status}
                    </span>
                  </td>
                  <td className="p-3 text-muted-foreground/80 text-xs">{b.created_at ? new Date(b.created_at).toLocaleDateString() : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {pages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="flex items-center gap-1 text-xs tracking-widest uppercase text-muted-foreground/80 hover:text-foreground transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-3 w-3" /> {t("admin.prev_page")}
            </button>
            <span className="text-xs text-muted-foreground/80">{t("admin.page_of", { page, pages })}</span>
            <button
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page >= pages}
              className="flex items-center gap-1 text-xs tracking-widest uppercase text-muted-foreground/80 hover:text-foreground transition-colors disabled:opacity-20 disabled:cursor-not-allowed"
            >
              {t("admin.next_page")} <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
